import { Alert, Button, Center, Group, Loader, Modal, Paper, Stack, Stepper, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { getQuestionnaireAnswers } from '@medplum/core';
import type { Bundle, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Logo, QuestionnaireForm } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { getIntakeBotClient } from '../../lib/bot-client';
import { botIdentifier } from '../../lib/bot-identifier';
import { getOrganizationId } from '../../utils/tenant';
import { PatientInfoForm } from './forms/PatientInfoForm';
import { ProviderInfoForm } from './forms/ProviderInfoForm';
import classes from './PCPReferralPage.module.css';
import { TriageForm } from './TriageForm';

// ─── Types ─────────────────────────────────────────────────────────────────

type Step =
  | 'loading'
  | 'error'
  | 'patient-info'
  | 'provider-info'
  | 'eligibility-check'
  | 'stopbang-form'
  | 'ess-form'
  | 'finished';

// Maps each step to the questionnaire name in public-questionnaires.json
// eligibility-check uses a custom TriageForm instead of QuestionnaireForm
const QUESTIONNAIRE_KEY: Partial<Record<Step, string>> = {
  'patient-info': 'pcp-referral-patient-info',
  'provider-info': 'pcp-referral-provider-info',
  'stopbang-form': 'stop-bang',
  'ess-form': 'epworth-sleepiness-scale',
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function getStepperActive(step: Step): number {
  switch (step) {
    case 'patient-info':
      return 0;
    case 'provider-info':
      return 1;
    case 'eligibility-check':
    case 'stopbang-form':
    case 'ess-form':
      return 2;
    case 'finished':
      return 3;
    default:
      return 0;
  }
}

function phaseToStep(phase: string, missing: string[]): Step {
  switch (phase) {
    case 'pcp-referral-phase-1-complete':
      return 'provider-info';
    case 'pcp-referral-phase-2-complete':
      return 'eligibility-check';
    case 'pcp-referral-pending-fillin':
      if (missing.includes('stopbang')) {
        return 'stopbang-form';
      }
      if (missing.includes('ess')) {
        return 'ess-form';
      }
      return 'eligibility-check';
    case 'pcp-referral-finished':
      return 'finished';
    // backward compat with patients created before this change
    case 'pcp-referral-phase-3-complete':
      return 'eligibility-check';
    case 'pcp-referral-pending-stopbang':
      return 'stopbang-form';
    case 'pcp-referral-pending-ess':
      return 'ess-form';
    default:
      return 'eligibility-check';
  }
}

function botId(name: string) {
  return botIdentifier(name);
}

// ─── Main Page ─────────────────────────────────────────────────────────────

async function executeBot(name: string, input: unknown): Promise<unknown> {
  const client = await getIntakeBotClient();
  return client.executeBot(botId(name), input, 'application/json');
}

export function PCPReferralPage(): JSX.Element {
  const { referralId: referralIdParam } = useParams<{ referralId?: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [backModalOpen, setBackModalOpen] = useState(false);
  const [reverting, setReverting] = useState(false);
  const qFormContainerRef = useRef<HTMLDivElement>(null);
  const [questionnaires, setQuestionnaires] = useState<Map<string, Questionnaire>>(new Map());
  // Stored between patient-info and provider-info steps (both needed for phase-1-2)
  const [patientResponse, setPatientResponse] = useState<QuestionnaireResponse | null>(null);

  // Load questionnaires + resolve phase on every referralIdParam change
  useEffect(() => {
    setStep('loading');

    const qFetch = fetch('/public-questionnaires.json')
      .then((res) => res.json())
      .then((bundle: Bundle) => {
        const map = new Map<string, Questionnaire>();
        for (const entry of bundle.entry ?? []) {
          const q = entry.resource;
          if (q?.resourceType === 'Questionnaire' && q.name) {
            map.set(q.name, q);
          }
        }
        return map;
      });

    const phaseFetch: Promise<{ phase: string; missing: string[] } | null> = referralIdParam
      ? (executeBot('pcp-referral-status', { referralId: referralIdParam }) as Promise<{
          phase: string;
          missing: string[];
        }>)
      : Promise.resolve(null);

    Promise.all([qFetch, phaseFetch])
      .then(([map, status]) => {
        setQuestionnaires(map);
        setStep(status !== null ? phaseToStep(status.phase, status.missing) : 'patient-info');
      })
      .catch((err) => {
        Sentry.captureException(err);
        setStep('error');
      });
  }, [referralIdParam]);

  // Step 1 — store patient QR and advance to provider info
  const handlePatientSubmit = useCallback((response: QuestionnaireResponse): void => {
    const answers = getQuestionnaireAnswers(response);
    const insuranceProvider = answers['insurance-provider']?.valueString;
    if (insuranceProvider === 'Other') {
      showNotification({
        color: 'red',
        title: 'Insurance not supported',
        message: 'We currently only support CareFirst insurance plans.',
        autoClose: true,
      });
      return;
    }
    setPatientResponse(response);
    setStep('provider-info');
  }, []);

  // Step 2 — call phase-1-2 (new referral) or phase-2 (re-submission after going back)
  const handleProviderSubmit = useCallback(
    async (response: QuestionnaireResponse): Promise<void> => {
      setSubmitting(true);
      try {
        if (referralIdParam) {
          // Patient already exists — re-run phase 2 only (provider info updated after going back)
          await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-phase-2' }, async () => {
            await executeBot('pcp-referral-phase-2', { referralId: referralIdParam, providerResponse: response });
          });
          setStep('eligibility-check');
        } else {
          // Fresh submission — create patient + practitioner
          if (!patientResponse) {
            showNotification({
              color: 'red',
              message: 'Patient information is missing. Please go back and try again.',
              autoClose: false,
            });
            return;
          }
          const result = await Sentry.startSpan(
            { op: 'bot.execute', name: 'Execute pcp-referral-phase-1-2' },
            async () => {
              return executeBot('pcp-referral-phase-1-2', {
                providerResponse: response,
                patientResponse,
                organizationId: getOrganizationId(),
              }) as Promise<{ referralId: string }>;
            }
          );
          await navigate(`/pcp-referral/${result.referralId}`);
        }
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Failed to submit referral information.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, patientResponse, referralIdParam]
  );

  // Step 3 — submit triage QR; inspect answers to set missing tags and route accordingly
  const handleTriageSubmit = useCallback(
    async (response: QuestionnaireResponse): Promise<void> => {
      const answers = getQuestionnaireAnswers(response);
      const needsStopBang = answers['stopbang-share-method']?.valueString === 'Fill in';
      const needsESS = answers['ess-share-method']?.valueString === 'Fill in';
      const missingForms: ('stopbang' | 'ess')[] = [
        ...(needsStopBang ? (['stopbang'] as const) : []),
        ...(needsESS ? (['ess'] as const) : []),
      ];

      const id = referralIdParam ?? '';
      setSubmitting(true);
      try {
        await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-step (triage)' }, async () => {
          await executeBot('pcp-referral-step', {
            referralId: id,
            phase: 'pcp-referral-phase-2-complete',
            questionnaireResponse: response,
            nextPhase: 'pcp-referral-pending-fillin',
            missingForms,
          });
        });

        if (needsStopBang) {
          setStep('stopbang-form');
        } else if (needsESS) {
          setStep('ess-form');
        } else {
          await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-finalize' }, async () => {
            await executeBot('pcp-referral-finalize', { referralId: id });
          });
          setStep('finished');
        }
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Failed to submit eligibility check.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [referralIdParam]
  );

  // Step 4a — submit STOP-BANG QR; use remainingMissing to route to ess or finish
  const handleStopBangSubmit = useCallback(
    async (response: QuestionnaireResponse): Promise<void> => {
      const id = referralIdParam ?? '';
      setSubmitting(true);
      try {
        const { remainingMissing } = await Sentry.startSpan(
          { op: 'bot.execute', name: 'Execute pcp-referral-fillin-step (stopbang)' },
          async () =>
            executeBot('pcp-referral-fillin-step', {
              referralId: id,
              formType: 'stopbang',
              questionnaireResponse: response,
            }) as Promise<{ remainingMissing: string[] }>
        );

        if (remainingMissing.includes('ess')) {
          setStep('ess-form');
        } else {
          await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-finalize' }, async () => {
            await executeBot('pcp-referral-finalize', { referralId: id });
          });
          setStep('finished');
        }
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Failed to submit STOP-BANG questionnaire.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [referralIdParam]
  );

  // Step 4b — submit ESS QR and finalize
  const handleESSSubmit = useCallback(
    async (response: QuestionnaireResponse): Promise<void> => {
      const id = referralIdParam ?? '';
      setSubmitting(true);
      try {
        await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-fillin-step (ess)' }, async () =>
          executeBot('pcp-referral-fillin-step', { referralId: id, formType: 'ess', questionnaireResponse: response })
        );
        await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-finalize' }, async () => {
          await executeBot('pcp-referral-finalize', { referralId: id });
        });
        setStep('finished');
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Failed to submit Epworth questionnaire.',
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [referralIdParam]
  );

  // Opens the confirmation modal — actual navigation happens in confirmBack
  const handleBack = useCallback((): void => {
    setBackModalOpen(true);
  }, []);

  // Called when the user confirms they want to go back
  const confirmBack = useCallback(async (): Promise<void> => {
    setBackModalOpen(false);

    if (step === 'provider-info') {
      // No FHIR data exists yet — just clear local state and return to patient info
      setPatientResponse(null);
      setStep('patient-info');
    } else if (step === 'eligibility-check') {
      // Revert the server-side workflow tag and delete the provider QR
      setReverting(true);
      try {
        await Sentry.startSpan({ op: 'bot.execute', name: 'Execute pcp-referral-revert-step' }, async () => {
          await executeBot('pcp-referral-revert-step', {
            referralId: referralIdParam,
            toPhase: 'pcp-referral-phase-1-complete',
          });
        });
        setStep('provider-info');
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: 'Failed to go back. Please try again.',
          autoClose: false,
        });
      } finally {
        setReverting(false);
      }
    } else if (step === 'stopbang-form' || step === 'ess-form') {
      const fromForm = step === 'stopbang-form' ? 'stopbang' : 'ess';
      setReverting(true);
      try {
        const { nextStep } = await Sentry.startSpan(
          { op: 'bot.execute', name: `Execute pcp-referral-revert-fillin (${fromForm})` },
          async () =>
            executeBot('pcp-referral-revert-fillin', { referralId: referralIdParam ?? '', fromForm }) as Promise<{
              nextStep: 'eligibility-check' | 'stopbang-form';
            }>
        );
        setStep(nextStep);
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: 'Failed to go back. Please try again.',
          autoClose: false,
        });
      } finally {
        setReverting(false);
      }
    }
  }, [step, referralIdParam]);

  const handleReset = useCallback(async (): Promise<void> => {
    setStep('patient-info');
    setPatientResponse(null);
    await navigate('/pcp-referral');
  }, [navigate]);

  // Resolve the QuestionnaireForm submit handler for the current step (STOP-BANG / ESS only —
  // patient-info / provider-info render dedicated Mantine forms below).
  const resolveSubmitHandler = (): ((response: QuestionnaireResponse) => void) | undefined => {
    switch (step) {
      case 'stopbang-form':
        return (r) => {
          handleStopBangSubmit(r).catch(console.error);
        };
      case 'ess-form':
        return (r) => {
          handleESSSubmit(r).catch(console.error);
        };
      default:
        return undefined;
    }
  };

  const submitHandler = resolveSubmitHandler();
  const questionnaireName = QUESTIONNAIRE_KEY[step];
  const questionnaire = questionnaireName ? questionnaires.get(questionnaireName) : undefined;
  const showBack =
    step === 'provider-info' || step === 'eligibility-check' || step === 'stopbang-form' || step === 'ess-form';
  const showStepper = step !== 'loading' && step !== 'error';

  return (
    <div className={classes.wrapper}>
      <Modal
        opened={backModalOpen}
        onClose={() => {
          setBackModalOpen(false);
        }}
        title="Go back?"
        centered
      >
        <Text size="sm" c="dimmed" mb="lg">
          Going back will permanently delete the information you entered for this step. This cannot be undone.
        </Text>
        <Group justify="flex-end">
          <Button
            variant="subtle"
            color="gray"
            onClick={() => {
              setBackModalOpen(false);
            }}
            disabled={reverting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => {
              confirmBack().catch(console.error);
            }}
            loading={reverting}
            disabled={reverting}
          >
            Go Back
          </Button>
        </Group>
      </Modal>

      <div className={classes.container}>
        <div className={classes.logo}>
          <Logo size={48} />
        </div>

        {/* Stepper card */}
        {showStepper && (
          <Paper withBorder p="xl" radius="md" shadow="sm">
            <Title order={2} ta="center" mb="lg">
              New Patient Referral Form
            </Title>
            <Stepper active={getStepperActive(step)} color="blue">
              <Stepper.Step
                label={
                  <span>
                    Patient Info<span className={classes.requiredStar}>*</span>
                  </span>
                }
                description="Tell us about the patient"
              />
              <Stepper.Step
                label={
                  <span>
                    Provider Info<span className={classes.requiredStar}>*</span>
                  </span>
                }
                description="Who is referring?"
              />
              <Stepper.Step
                label={
                  <span>
                    Eligibility Check<span className={classes.requiredStar}>*</span>
                  </span>
                }
                description="Does patient qualify for HSAT?"
              />
            </Stepper>
          </Paper>
        )}

        {/* Form card */}
        <Paper withBorder p="xl" radius="md" shadow="sm" mt="md">
          {step === 'loading' && (
            <Center py="xl">
              <Loader />
            </Center>
          )}

          {step === 'error' && (
            <Stack align="center" gap="md" py="xl">
              <Alert color="red" title="Something went wrong">
                Unable to load referral status. Please try again or contact support.
              </Alert>
              <Button
                variant="subtle"
                color="blue"
                onClick={() => {
                  handleReset().catch(console.error);
                }}
              >
                Start a new referral
              </Button>
            </Stack>
          )}

          {step === 'finished' && (
            <Stack align="center" gap="md" py="xl">
              <Title order={2}>Referral Received!</Title>
              <Text c="dimmed">We will contact the patient within 1–3 business days.</Text>
              <Button
                color="blue"
                mt="md"
                onClick={() => {
                  handleReset().catch(console.error);
                }}
              >
                Submit Another Referral
              </Button>
            </Stack>
          )}

          {step === 'eligibility-check' && (
            <TriageForm
              submitting={submitting}
              onSubmit={(r) => {
                handleTriageSubmit(r).catch(console.error);
              }}
              onBack={showBack ? handleBack : undefined}
            />
          )}

          {step === 'patient-info' &&
            questionnaires.get('pcp-referral-patient-info') &&
            (() => {
              const q = questionnaires.get('pcp-referral-patient-info');
              return q ? (
                <PatientInfoForm questionnaire={q} onSubmit={handlePatientSubmit} submitting={submitting} />
              ) : null;
            })()}

          {step === 'provider-info' &&
            questionnaires.get('pcp-referral-provider-info') &&
            (() => {
              const q = questionnaires.get('pcp-referral-provider-info');
              return q ? (
                <ProviderInfoForm
                  questionnaire={q}
                  onSubmit={(r) => {
                    handleProviderSubmit(r).catch(console.error);
                  }}
                  onBack={showBack ? handleBack : undefined}
                  submitting={submitting}
                />
              ) : null;
            })()}

          {(step === 'stopbang-form' || step === 'ess-form') && questionnaire && submitHandler && (
            <>
              <div ref={qFormContainerRef}>
                <QuestionnaireForm
                  key={step}
                  questionnaire={questionnaire}
                  onSubmit={submitHandler}
                  excludeButtons={true}
                />
              </div>
              <Group justify="flex-end" mt="md">
                {showBack && (
                  <Button variant="subtle" color="blue" onClick={handleBack} disabled={submitting}>
                    Back
                  </Button>
                )}
                <Button
                  loading={submitting}
                  disabled={submitting}
                  onClick={() => {
                    qFormContainerRef.current?.querySelector('form')?.requestSubmit();
                  }}
                >
                  {submitting ? 'Submitting…' : 'Continue'}
                </Button>
              </Group>
            </>
          )}
        </Paper>
      </div>
    </div>
  );
}
